import React, {useEffect, useMemo, useRef, useState} from "react";
import {AppContext} from "../../HomePage";
import {AiJobService} from "../../service/AiJobService";
import {ScraperService} from "../../service/ScraperService";
import {DashboardService} from "../../service/DashboardService";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {Dialog} from "primereact/dialog";
import {InputTextarea} from "primereact/inputtextarea";
import {OverlayPanel} from "primereact/overlaypanel";
import {Loading} from "../../utils/InlineComponents";
import {exportFile, getLinkFromUrl} from "../../utils/Utils";
import {toDateTimeString} from "../../utils/TimeUtil";

const AiJobsMonitor = () => {

    const {t, toast, setError} = React.useContext(AppContext);
    const aiJobService = useMemo(() => new AiJobService(), []);
    const scraperService = useMemo(() => new ScraperService(), []);
    const dashboardService = useMemo(() => new DashboardService(), []);
    const aiJobActionOverlayRef = useRef(null);

    const [aiJobs, setAiJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const emptyFilters = {status: null, provider: null, model_name: null, operation: null};
    const [filters, setFilters] = useState(emptyFilters);
    const [selectedAiJobDetails, setSelectedAiJobDetails] = useState(null);
    const [selectedExceptionInfo, setSelectedExceptionInfo] = useState(null);
    const [selectedWebsiteDetailJob, setSelectedWebsiteDetailJob] = useState(null);
    const [websiteDetailLoadingJobId, setWebsiteDetailLoadingJobId] = useState(null);
    const [selectedExceptionDetail, setSelectedExceptionDetail] = useState(null);
    const [exceptionDetailLoadingJobId, setExceptionDetailLoadingJobId] = useState(null);
    const [copyingFailureResponseHash, setCopyingFailureResponseHash] = useState(null);
    const [responseDownloadLoadingHash, setResponseDownloadLoadingHash] = useState(null);
    const [manualPromptData, setManualPromptData] = useState(null);
    const [manualPromptLoadingHash, setManualPromptLoadingHash] = useState(null);
    const [selectedApproveJob, setSelectedApproveJob] = useState(null);
    const [approvingAiJobId, setApprovingAiJobId] = useState(null);
    const [selectedDeleteJob, setSelectedDeleteJob] = useState(null);
    const [deletingAiJobId, setDeletingAiJobId] = useState(null);
    const [selectedCorrectionJob, setSelectedCorrectionJob] = useState(null);
    const [correctionReviewNote, setCorrectionReviewNote] = useState("");
    const [correctionLoading, setCorrectionLoading] = useState(false);
    const [correctionError, setCorrectionError] = useState(null);
    const [selectedActionJob, setSelectedActionJob] = useState(null);
    const [aiJobLogsLoading, setAiJobLogsLoading] = useState(false);
    const [expandedAiJobLogRows, setExpandedAiJobLogRows] = useState(null);

    const activeStatuses = [
        "queued",
        "cloning_repo",
        "analyzing_error",
        "generating_patch",
        "applying_patch",
        "creating_pull_request",
        "review_correction_queued",
        "applying_review_correction"
    ];

    const knownStatuses = [
        ...activeStatuses,
        "waiting_user_review",
        "merging_pull_request",
        "deploying_prod",
        "deleting_branch",
        "completed",
        "failed",
        "correction_failed",
        "approval_failed"
    ];

    const sortAiJobs = (jobs) => {
        return [...(jobs || [])].sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0));
    };

    const sortAiJobLogs = (logs) => {
        return [...(logs || [])].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    };

    const compactParams = (params) => {
        return Object.keys(params).reduce((result, key) => {
            if (params[key])
                result[key] = params[key];
            return result;
        }, {});
    };

    const listAiJobs = React.useCallback(() => {
        setLoading(true);
        aiJobService.listJobs({limit: 100, ...compactParams(filters)}).then(data => {
            setAiJobs(sortAiJobs(data));
            setLoading(false);
        }).catch(error => {
            setLoading(false);
            setError(error);
            setError(null);
        });
    }, [aiJobService, filters, setError]);

    useEffect(() => {
        listAiJobs();
    }, [listAiJobs]);

    const updateAiJobInList = (updatedJob) => {
        setAiJobs(currentJobs => {
            const existingJob = currentJobs.find(job => job.id === updatedJob.id);
            const normalizedJob = updatedJob && updatedJob.logs ? {...updatedJob, logs: sortAiJobLogs(updatedJob.logs)} : updatedJob;
            return sortAiJobs([{...existingJob, ...normalizedJob}, ...currentJobs.filter(job => job.id !== updatedJob.id)]);
        });
    };

    const refreshAiJob = (job) => {
        aiJobService.getJob(job.id).then(data => {
            updateAiJobInList(data);
            if (selectedAiJobDetails && selectedAiJobDetails.id === data.id) {
                setSelectedAiJobDetails({...selectedAiJobDetails, ...data, logs: data.logs ? sortAiJobLogs(data.logs) : selectedAiJobDetails.logs});
            }
        }).catch(error => {
            setError(error);
            setError(null);
        });
    };

    const openAiJobDetails = (job) => {
        setSelectedAiJobDetails(job);
        setExpandedAiJobLogRows(null);
        aiJobService.getJob(job.id).then(data => {
            const normalizedJob = data.logs ? {...data, logs: sortAiJobLogs(data.logs)} : data;
            updateAiJobInList(normalizedJob);
            setSelectedAiJobDetails({...job, ...normalizedJob});
        }).catch(error => {
            setError(error);
            setError(null);
        });
    };

    const getAiJobErrorMessage = (error) => {
        const data = error && error.response && error.response.data ? error.response.data : null;
        return (data && data.error) ||
            (data && data.data && data.data.error_message) ||
            (data && data.message) ||
            t('ai_job.request_failed');
    };

    const getAiJobApprovalErrorMessage = (error) => {
        const data = error && error.response && error.response.data ? error.response.data : null;
        return (data && data.error) ||
            (data && data.data && data.data.error_message) ||
            (data && data.message);
    };

    const canApproveAiJob = (job) => {
        return job && job.status === "waiting_user_review" && job.pull_request_url;
    };

    const canRequestAiJobCorrection = (job) => {
        return job && (job.status === "waiting_user_review" || job.status === "correction_failed");
    };

    const openAiJobCorrectionDialog = (job) => {
        setSelectedCorrectionJob(job);
        setCorrectionReviewNote("");
        setCorrectionError(null);
    };

    const approveAiJob = () => {
        if (!selectedApproveJob)
            return;

        setApprovingAiJobId(selectedApproveJob.id);
        aiJobService.approveJob(selectedApproveJob.id).then(data => {
            updateAiJobInList(data);
            if (selectedAiJobDetails && selectedAiJobDetails.id === data.id) {
                setSelectedAiJobDetails({...selectedAiJobDetails, ...data});
            }
            if (data.status === "completed") {
                toast.current.show({severity: 'success', summary: t('ai_job.approved_deployed'), detail: t('message_detail.successful')});
            }
            if (data.status === "approval_failed") {
                toast.current.show({severity: 'error', summary: t('ai_job.approval_failed'), detail: data.error_message || t('ai_job.approval_failed')});
            }
            setSelectedApproveJob(null);
            setApprovingAiJobId(null);
        }).catch(error => {
            if (error && error.response && error.response.status === 401) {
                setError(error);
                setError(null);
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: t('ai_job.approval_failed'),
                    detail: error && error.response && error.response.status === 400
                        ? (getAiJobApprovalErrorMessage(error) || t('ai_job.cannot_approve_current_status'))
                        : getAiJobErrorMessage(error)
                });
            }
            setSelectedApproveJob(null);
            setApprovingAiJobId(null);
        });
    };

    const applyAiJobCorrection = () => {
        if (!selectedCorrectionJob || !correctionReviewNote.trim())
            return;

        setCorrectionLoading(true);
        setCorrectionError(null);
        aiJobService.applyCorrection(selectedCorrectionJob.id, correctionReviewNote.trim()).then(data => {
            updateAiJobInList(data);
            if (selectedAiJobDetails && selectedAiJobDetails.id === data.id) {
                setSelectedAiJobDetails({...selectedAiJobDetails, ...data});
            }
            setSelectedCorrectionJob(null);
            setCorrectionReviewNote("");
            setCorrectionLoading(false);
            toast.current.show({severity: 'success', summary: t('ai_job.correction_applied'), detail: t('message_detail.successful')});
            refreshAiJobLogs(data.id);
        }).catch(error => {
            if (error && error.response && error.response.status === 401) {
                setError(error);
                setError(null);
            }
            setCorrectionError(getAiJobErrorMessage(error));
            setCorrectionLoading(false);
        });
    };

    const deleteAiJob = () => {
        if (!selectedDeleteJob)
            return;

        setDeletingAiJobId(selectedDeleteJob.id);
        aiJobService.deleteJob(selectedDeleteJob.id).then(() => {
            setAiJobs(currentJobs => currentJobs.filter(job => job.id !== selectedDeleteJob.id));
            if (selectedAiJobDetails && selectedAiJobDetails.id === selectedDeleteJob.id) {
                setSelectedAiJobDetails(null);
            }
            toast.current.show({severity: 'success', summary: t('ai_job.deleted'), detail: t('message_detail.successful')});
            setSelectedDeleteJob(null);
            setDeletingAiJobId(null);
        }).catch(error => {
            if (error && error.response && error.response.status === 401) {
                setError(error);
                setError(null);
            } else {
                toast.current.show({severity: 'error', summary: t('ai_job.delete_failed'), detail: getAiJobErrorMessage(error)});
            }
            setSelectedDeleteJob(null);
            setDeletingAiJobId(null);
        });
    };

    const refreshAiJobLogs = (jobId) => {
        if (!jobId)
            return;

        setAiJobLogsLoading(true);
        aiJobService.getJobLogs(jobId).then(data => {
            const logs = sortAiJobLogs(data);
            updateAiJobInList({id: jobId, logs: logs});
            setSelectedAiJobDetails(currentJob => currentJob && currentJob.id === jobId ? {...currentJob, logs: logs} : currentJob);
            setExpandedAiJobLogRows(null);
            setAiJobLogsLoading(false);
        }).catch(error => {
            setAiJobLogsLoading(false);
            setError(error);
            setError(null);
        });
    };

    const getStatusLabel = (status) => {
        const statusMap = {
            queued: t('ai_job.status_queued'),
            cloning_repo: t('ai_job.status_cloning_repo'),
            analyzing_error: t('ai_job.status_analyzing_error'),
            generating_patch: t('ai_job.status_generating_patch'),
            applying_patch: t('ai_job.status_applying_patch'),
            creating_pull_request: t('ai_job.status_creating_pull_request'),
            waiting_user_review: t('ai_job.waiting_for_review'),
            review_correction_queued: t('ai_job.status_review_correction_queued'),
            applying_review_correction: t('ai_job.status_applying_review_correction'),
            merging_pull_request: t('ai_job.merging_pull_request'),
            deploying_prod: t('ai_job.deploying_prod'),
            deleting_branch: t('ai_job.deleting_branch'),
            completed: t('ai_job.completed'),
            failed: t('ai_job.failed'),
            correction_failed: t('ai_job.correction_failed'),
            approval_failed: t('ai_job.approval_failed')
        };
        return statusMap[status] || status || "-";
    };

    const statusTemplate = (job) => {
        const statusClass = job.status === "completed" ? "p-tag p-tag-success" :
            (job.status === "failed" || job.status === "correction_failed" || job.status === "approval_failed") ? "p-tag p-tag-danger" :
                job.status === "waiting_user_review" ? "p-tag p-tag-warning" : "p-tag p-tag-info";
        return <span className={statusClass}>{getStatusLabel(job.status)}</span>;
    };

    const dateTimeTemplate = (value) => {
        return value ? toDateTimeString(value) : "-";
    };

    const getNestedValue = (job, keys) => {
        for (let i = 0; i < keys.length; i++) {
            const parts = keys[i].split(".");
            let value = job;
            for (let j = 0; j < parts.length; j++) {
                value = value && value[parts[j]] !== undefined ? value[parts[j]] : null;
            }
            if (value !== null && value !== undefined && value !== "")
                return value;
        }
        return null;
    };

    const summaryTemplate = (job) => {
        const summaryValue = job.operation === "add-website-spider"
            ? getNestedValue(job, [
                "summary.website_url",
                "summary.url",
                "summary.website",
                "input.website_url",
                "input.url",
                "input.undefined_website_url"
            ])
            : getNestedValue(job, ["summary.exception_message"]);

        if (!summaryValue)
            return "-";
        if (typeof summaryValue === "object")
            return <pre style={{margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "18em"}}>{JSON.stringify(summaryValue)}</pre>;
        return <div title={summaryValue.toString()} style={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "18em"}}>{summaryValue.toString()}</div>;
    };

    const exceptionInfoValueTemplate = (value) => {
        if (React.isValidElement(value))
            return value;
        if (value === null || value === undefined || value === "")
            return "-";
        if (Array.isArray(value))
            return value.length > 0 ? value : "-";
        if (typeof value === "object")
            return <pre style={{whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: "18em", overflow: "auto", border: "1px solid #ddd", padding: "1em", marginTop: ".25em"}}>{JSON.stringify(value, null, 2)}</pre>;
        if (typeof value === "string" && value.indexOf("http") === 0)
            return getLinkFromUrl(value);
        return <span style={{whiteSpace: "pre-wrap", wordBreak: "break-word"}}>{value.toString()}</span>;
    };

    const exceptionInfoDialogTemplate = () => {
        if (!selectedExceptionInfo)
            return null;

        const entries = Object.entries(selectedExceptionInfo);

        return <Dialog header={t('ai_job.exception_info')} visible={!!selectedExceptionInfo} style={{maxWidth: '70vw', minWidth: '45vw'}} modal={true}
                       onHide={() => setSelectedExceptionInfo(null)}>
            <div className="p-grid">
                {entries.length === 0
                    ?
                    <div className="p-col-12">-</div>
                    :
                    entries.map(([key, value]) =>
                        <div className={typeof value === "object" ? "p-col-12" : "p-col-12 p-md-6"} key={key}>
                            <b>{key}</b>
                            <div>{exceptionInfoValueTemplate(value)}</div>
                        </div>
                    )
                }
            </div>
        </Dialog>;
    };

    const getWebsiteDetailValue = (job, keys) => {
        return getNestedValue(job, keys);
    };

    const getUndefinedWebsiteId = (job) => {
        return getNestedValue(job, [
            "input.undefined_website_id",
            "summary.undefined_website_id",
            "undefined_website.id",
            "undefined_website._id"
        ]);
    };

    const normalizeUndefinedWebsiteResponse = (data, undefinedWebsiteId) => {
        if (Array.isArray(data))
            return data.find(item => item.id === undefinedWebsiteId || item._id === undefinedWebsiteId) || data[0] || null;
        return data || null;
    };

    const openWebsiteDetail = (job) => {
        setSelectedWebsiteDetailJob(job);
        const undefinedWebsiteId = getUndefinedWebsiteId(job);
        if (!undefinedWebsiteId)
            return;

        setWebsiteDetailLoadingJobId(job.id);
        dashboardService.getUndefinedWebsiteById(undefinedWebsiteId).then(data => {
            const undefinedWebsite = normalizeUndefinedWebsiteResponse(data, undefinedWebsiteId);
            setSelectedWebsiteDetailJob(currentJob => currentJob && currentJob.id === job.id ? {...currentJob, undefined_website: undefinedWebsite} : currentJob);
            setWebsiteDetailLoadingJobId(null);
        }).catch(error => {
            setWebsiteDetailLoadingJobId(null);
            setError(error);
            setError(null);
        });
    };

    const getWebsiteSampleProductUrls = (job) => {
        const urls = [];
        const addUrl = (url) => {
            if (url && !urls.includes(url))
                urls.push(url);
        };
        const addValue = (value) => {
            if (Array.isArray(value))
                value.forEach(addUrl);
            else
                addUrl(value);
        };

        addValue(getWebsiteDetailValue(job, ["summary.sample_product_url", "input.sample_product_url", "undefined_website.sample_product_url"]));
        addValue(getWebsiteDetailValue(job, ["summary.sample_product_urls", "input.sample_product_urls", "undefined_website.sample_product_urls"]));
        return urls;
    };

    const websiteDetailDialogTemplate = () => {
        if (!selectedWebsiteDetailJob)
            return null;

        const job = selectedWebsiteDetailJob;
        const isLoading = websiteDetailLoadingJobId === job.id;
        const website = getWebsiteDetailValue(job, [
            "summary.website_url",
            "summary.url",
            "summary.website",
            "input.website_url",
            "input.url",
            "input.undefined_website_url",
            "undefined_website.url",
            "undefined_website.website_url"
        ]);
        const userEmail = getWebsiteDetailValue(job, ["summary.user_email", "input.user_email", "undefined_website.user_email"]);
        const userName = getWebsiteDetailValue(job, ["summary.user_name", "input.user_name", "undefined_website.user_name"]);
        const campaignName = getWebsiteDetailValue(job, ["summary.campaign_name", "input.campaign_name", "undefined_website.campaign_name"]);
        const creationDate = getWebsiteDetailValue(job, ["summary.created_date", "input.created_date", "undefined_website.created_date"]);
        const baseSpider = getWebsiteDetailValue(job, ["summary.used_base_spider", "summary.base_spider", "input.used_base_spider", "input.base_spider", "undefined_website.used_base_spider"]);
        const sampleProductUrls = getWebsiteSampleProductUrls(job);

        return <Dialog header={t('ai_job.website_detail')} visible={!!selectedWebsiteDetailJob} style={{maxWidth: '70vw', minWidth: '45vw'}} modal={true}
                       onHide={() => setSelectedWebsiteDetailJob(null)}>
            <div className="p-grid">
                {isLoading &&
                    <div className="p-col-12">
                        <span className="p-tag p-tag-info">{t('ai_job.loading_website_detail')}</span>
                    </div>
                }
                <div className="p-col-12 p-md-6">
                    <b>{t('ai_job.website')}</b>
                    <div style={{wordBreak: "break-word"}}>{website ? getLinkFromUrl(website) : "-"}</div>
                </div>
                <div className="p-col-12 p-md-6">
                    <b>{t('user.email')}</b>
                    <div style={{wordBreak: "break-word"}}>{userEmail || "-"}</div>
                </div>
                <div className="p-col-12 p-md-6">
                    <b>{t('ai_job.website_creation_date')}</b>
                    <div>{creationDate ? dateTimeTemplate(creationDate) : "-"}</div>
                </div>
                <div className="p-col-12 p-md-6">
                    <b>{t('user.name')}</b>
                    <div>{userName || "-"}</div>
                </div>
                <div className="p-col-12 p-md-6">
                    <b>{t('campaign.campaign')}</b>
                    <div>{campaignName || "-"}</div>
                </div>
                <div className="p-col-12 p-md-6">
                    <b>{t('campaign.competitors.base_spider')}</b>
                    <div>{baseSpider || "-"}</div>
                </div>
                <div className="p-col-12">
                    <b>{t('ai_job.sample_product_urls')}</b>
                    {sampleProductUrls.length === 0
                        ? <div>-</div>
                        : <ul style={{marginTop: ".5em"}}>
                            {sampleProductUrls.map((url, index) =>
                                <li key={index} style={{wordBreak: "break-word"}}>
                                    <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
                                </li>
                            )}
                        </ul>}
                </div>
            </div>
        </Dialog>;
    };

    const normalizeFailureDetailResponse = (data, exceptionHash) => {
        if (Array.isArray(data))
            return data;
        if (data && data.failure_details && Array.isArray(data.failure_details))
            return data.failure_details;
        if (data && data.response_text)
            return [{...data, response_text: undefined}];
        if (data)
            return [data];
        return [{exception_hash: exceptionHash}];
    };

    const openFixSpiderExceptionDetail = (job) => {
        const exceptionHash = getNestedValue(job, ["input.exception_hash"]);
        if (!exceptionHash)
            return;

        setExceptionDetailLoadingJobId(job.id);
        scraperService.getFailureResponse(exceptionHash).then(data => {
            setSelectedExceptionDetail({
                exception_hash: exceptionHash,
                failure_details: normalizeFailureDetailResponse(data, exceptionHash)
            });
            setExceptionDetailLoadingJobId(null);
        }).catch(error => {
            setExceptionDetailLoadingJobId(null);
            setError(error);
            setError(null);
        });
    };

    const exceptionDetailRowTemplate = (label, value) => {
        return <div className="p-grid p-col-12">
            <div className="p-col-3"><b>{label}</b></div>
            <div className="p-col-9">{exceptionInfoValueTemplate(value)}</div>
        </div>;
    };

    const exceptionDetailObjectTemplate = (detail) => {
        if (!detail || Object.keys(detail).length === 0)
            return "-";

        return <div style={{maxWidth: "100%", whiteSpace: "normal", wordBreak: "break-word"}}>
            {Object.entries(detail).map(([key, value]) =>
                <div key={key} style={{marginBottom: ".5em"}}>
                    <b>{key}: </b>{exceptionInfoValueTemplate(value)}
                </div>
            )}
        </div>;
    };

    const exceptionDetailProductLinksTemplate = (failureDetails) => {
        const urls = (failureDetails || []).flatMap(failureDetail => [
            failureDetail.response_url,
            failureDetail.product_url,
            failureDetail.competitor_url,
            failureDetail.failure_detail_competitor_url,
            failureDetail.failure_detail_url,
            failureDetail.redirect_url
        ]).filter(url => !!url);
        const uniqueUrls = [...new Set(urls)];

        if (uniqueUrls.length === 0)
            return "-";

        return uniqueUrls.slice(0, 5).map((url, index) =>
            <span key={url}>
                <a href={url} target="_blank" rel="noopener noreferrer" style={{marginRight: ".75em"}}>{"Product " + (index + 1)}</a>
            </span>
        );
    };

    const copyFailureResponse = (exceptionHash) => {
        if (!exceptionHash)
            return;

        setCopyingFailureResponseHash(exceptionHash);
        scraperService.getFailureResponse(exceptionHash).then(data => {
            navigator.clipboard.writeText(data && data.response_text ? data.response_text : "").then(() => {
                toast.current.show({severity: 'success', summary: t('actions.copied'), detail: t('message_detail.successful')});
                setCopyingFailureResponseHash(null);
            }).catch(error => {
                setError(error);
                setError(null);
                setCopyingFailureResponseHash(null);
            });
        }).catch(error => {
            setError(error);
            setError(null);
            setCopyingFailureResponseHash(null);
        });
    };

    const getFailureResponseFilename = (exceptionHash, response) => {
        const contentDisposition = response && response.headers ? response.headers["content-disposition"] : null;
        const filenameMatch = contentDisposition ? contentDisposition.match(/filename="?([^"]+)"?/) : null;
        if (filenameMatch && filenameMatch[1])
            return filenameMatch[1];
        return "response_" + (exceptionHash || "").substring(0, 16) + ".txt";
    };

    const downloadFailureResponse = (exceptionHash) => {
        if (!exceptionHash)
            return;

        setResponseDownloadLoadingHash(exceptionHash);
        scraperService.downloadFailureResponse(exceptionHash).then(response => {
            exportFile(response, getFailureResponseFilename(exceptionHash, response));
            setResponseDownloadLoadingHash(null);
        }).catch(error => {
            setResponseDownloadLoadingHash(null);
            setError(error);
            setError(null);
        });
    };

    const buildManualPrompt = (exceptionHash) => {
        if (!exceptionHash)
            return;

        setManualPromptLoadingHash(exceptionHash);
        aiJobService.buildFixSpiderPrompt(exceptionHash).then(data => {
            setManualPromptData(data);
            setManualPromptLoadingHash(null);
        }).catch(error => {
            setManualPromptLoadingHash(null);
            setError(error);
            setError(null);
        });
    };

    const copyManualPrompt = () => {
        if (!manualPromptData || !manualPromptData.prompt)
            return;

        navigator.clipboard.writeText(manualPromptData.prompt).then(() => {
            toast.current.show({severity: 'success', summary: t('actions.copied'), detail: t('message_detail.successful')});
        }).catch(error => {
            setError(error);
            setError(null);
        });
    };

    const manualPromptDialogFooter = () => {
        return <div style={{whiteSpace: "nowrap"}}>
            <Button label={t('actions.copy_prompt')}
                    icon="pi pi-copy"
                    className="p-button-outlined p-button-info"
                    disabled={!manualPromptData || !manualPromptData.prompt}
                    onClick={copyManualPrompt}
                    style={{marginRight: ".5em"}}/>
            <Button label={t('actions.close')} icon="pi pi-times" className="p-button-outlined p-button-danger" onClick={() => setManualPromptData(null)}/>
        </div>;
    };

    const manualPromptDialogTemplate = () => {
        if (!manualPromptData)
            return null;

        return <Dialog header={t('ai_job.manual_fix_prompt')} visible={!!manualPromptData} style={{maxWidth: '75vw', minWidth: '55vw'}} modal={true}
                       footer={manualPromptDialogFooter()}
                       onHide={() => setManualPromptData(null)}>
            <div className="p-grid">
                <div className="p-col-12 p-md-6">
                    <b>{t('ai_job.exception_hash')}</b>
                    <div style={{wordBreak: "break-word"}}>{manualPromptData.exception_hash || "-"}</div>
                </div>
                <div className="p-col-12 p-md-6">
                    <b>{t('ai_job.response_filename')}</b>
                    <div style={{wordBreak: "break-word"}}>{manualPromptData.response_filename || "-"}</div>
                </div>
                <div className="p-col-12 p-md-6">
                    <b>{t('ai_job.response_text_size')}</b>
                    <div>{manualPromptData.response_text_size !== null && manualPromptData.response_text_size !== undefined ? manualPromptData.response_text_size : "-"}</div>
                </div>
                <div className="p-col-12 p-md-6">
                    <b>{t('ai_job.response_download_url')}</b>
                    <div style={{wordBreak: "break-word"}}>{manualPromptData.response_download_url || "-"}</div>
                </div>
                <div className="p-col-12">
                    <b>{t('ai_job.prompt_text')}</b>
                    <pre style={{whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: "32em", overflow: "auto", fontFamily: "monospace", border: "1px solid #ddd", padding: "1em", marginTop: ".5em"}}>{manualPromptData.prompt || "-"}</pre>
                </div>
            </div>
        </Dialog>;
    };

    const approveAiJobConfirmationFooter = () => {
        return <div>
            <Button label={t('actions.cancel')}
                    className="p-button-outlined"
                    disabled={!!approvingAiJobId}
                    onClick={() => setSelectedApproveJob(null)}
                    style={{marginRight: ".5em"}}/>
            <Button label={approvingAiJobId ? t('ai_job.approving') : t('actions.approve_and_deploy')}
                    icon={approvingAiJobId ? "pi pi-spin pi-spinner" : "pi pi-check"}
                    className="p-button-warning"
                    disabled={!!approvingAiJobId}
                    onClick={approveAiJob}/>
        </div>;
    };

    const aiJobCorrectionDialogFooter = () => {
        return <div>
            <Button label={t('actions.cancel')}
                    className="p-button-outlined"
                    disabled={correctionLoading}
                    onClick={() => setSelectedCorrectionJob(null)}
                    style={{marginRight: ".5em"}}/>
            <Button label={correctionLoading ? t('ai_job.correction_preparing') : t('actions.request_change')}
                    icon={correctionLoading ? "pi pi-spin pi-spinner" : "pi pi-comment"}
                    className="p-button-help"
                    disabled={correctionLoading || !correctionReviewNote.trim()}
                    onClick={applyAiJobCorrection}/>
        </div>;
    };

    const deleteAiJobConfirmationFooter = () => {
        return <div>
            <Button label={t('actions.cancel')}
                    className="p-button-outlined"
                    disabled={!!deletingAiJobId}
                    onClick={() => setSelectedDeleteJob(null)}
                    style={{marginRight: ".5em"}}/>
            <Button label={deletingAiJobId ? t('ai_job.deleting') : t('actions.delete')}
                    icon={deletingAiJobId ? "pi pi-spin pi-spinner" : "pi pi-trash"}
                    className="p-button-danger"
                    disabled={!!deletingAiJobId}
                    onClick={deleteAiJob}/>
        </div>;
    };

    const aiJobCorrectionDialogTemplate = () => {
        if (!selectedCorrectionJob)
            return null;

        return <Dialog header={t('ai_job.request_correction_title')} visible={!!selectedCorrectionJob} style={{maxWidth: '70vw', minWidth: '50vw'}} modal={true}
                       footer={aiJobCorrectionDialogFooter()}
                       onHide={() => !correctionLoading && setSelectedCorrectionJob(null)}>
            <div className="p-grid">
                {correctionError &&
                    <div className="p-col-12">
                        <span className="p-tag p-tag-danger" style={{whiteSpace: "normal"}}>{correctionError}</span>
                    </div>
                }
                {correctionLoading &&
                    <div className="p-col-12">
                        <span className="p-tag p-tag-info">{t('ai_job.correction_preparing')}</span>
                    </div>
                }
                <div className="p-col-12">
                    <b>{t('ai_job.pull_request_url')}</b>
                    <div style={{wordBreak: "break-word"}}>
                        {selectedCorrectionJob.pull_request_url
                            ? <a href={selectedCorrectionJob.pull_request_url} target="_blank" rel="noopener noreferrer">{selectedCorrectionJob.pull_request_url}</a>
                            : "-"}
                    </div>
                </div>
                <div className="p-col-12">
                    <b>{t('ai_job.branch_name')}</b>
                    <div style={{wordBreak: "break-word"}}>{selectedCorrectionJob.branch_name || "-"}</div>
                </div>
                <div className="p-col-12">
                    <b>{t('ai_job.change_summary')}</b>
                    <pre style={{whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: "14em", overflow: "auto"}}>{selectedCorrectionJob.diff || "-"}</pre>
                </div>
                <div className="p-col-12">
                    <b>{t('ai_job.diagnosis')}</b>
                    <pre style={{whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: "14em", overflow: "auto"}}>{selectedCorrectionJob.diagnosis || "-"}</pre>
                </div>
                <div className="p-col-12">
                    <b>{t('ai_job.review_note')}</b>
                    <InputTextarea value={correctionReviewNote}
                                   onChange={(e) => setCorrectionReviewNote(e.target.value)}
                                   rows={6}
                                   style={{width: "100%", marginTop: ".5em"}}
                                   disabled={correctionLoading}/>
                </div>
            </div>
        </Dialog>;
    };

    const exceptionDetailDialogTemplate = () => {
        if (!selectedExceptionDetail)
            return null;

        const failureDetails = selectedExceptionDetail.failure_details || [];
        const firstFailureDetail = failureDetails.length > 0 ? failureDetails[0] : selectedExceptionDetail;
        const detail = firstFailureDetail.exception_detail || {};
        const exceptionHash = selectedExceptionDetail.exception_hash || firstFailureDetail.exception_hash;
        const isCopying = copyingFailureResponseHash === exceptionHash;
        const isDownloading = responseDownloadLoadingHash === exceptionHash;
        const isBuildingPrompt = manualPromptLoadingHash === exceptionHash;

        return <Dialog header="Exception Detail" visible={!!selectedExceptionDetail} style={{maxWidth: '80vw', minWidth: '50vw'}} modal={true}
                       onHide={() => setSelectedExceptionDetail(null)}>
            {firstFailureDetail.redirect_url && exceptionDetailRowTemplate(t('scraping_request.redirect_url'), getLinkFromUrl(firstFailureDetail.redirect_url))}
            {exceptionDetailRowTemplate("Product", exceptionDetailProductLinksTemplate(failureDetails))}
            <div className="p-grid p-col-12">
                <div className="p-col-3"><b>Exception Detail</b></div>
                <div className="p-col-9">{exceptionDetailObjectTemplate(detail)}</div>
            </div>
            {exceptionHash &&
                <div className="p-grid p-col-12">
                    <div className="p-col-fixed">
                        <Button label={t('actions.copy_response_body')}
                                icon={isCopying ? "pi pi-spin pi-spinner" : "pi pi-copy"}
                                className="p-button-outlined p-button-success"
                                disabled={isCopying}
                                onClick={() => copyFailureResponse(exceptionHash)}/>
                        <Button label={t('actions.download_response')}
                                icon={isDownloading ? "pi pi-spin pi-spinner" : "pi pi-download"}
                                className="p-button-outlined p-button-info"
                                disabled={isDownloading}
                                onClick={() => downloadFailureResponse(exceptionHash)}
                                style={{marginLeft: ".5em"}}/>
                        <Button label={t('actions.build_manual_prompt')}
                                icon={isBuildingPrompt ? "pi pi-spin pi-spinner" : "pi pi-bolt"}
                                className="p-button-outlined p-button-warning"
                                disabled={isBuildingPrompt}
                                onClick={() => buildManualPrompt(exceptionHash)}
                                style={{marginLeft: ".5em"}}/>
                    </div>
                </div>
            }
        </Dialog>;
    };

    const getAiJobActionMenuItems = (job) => {
        const items = [];

        if (canApproveAiJob(job)) {
            items.push({
                label: approvingAiJobId === job.id ? t('ai_job.approving') : t('actions.approve_and_deploy'),
                icon: approvingAiJobId === job.id ? "pi pi-spin pi-spinner" : "pi pi-check",
                className: "p-button-success",
                disabled: !!approvingAiJobId,
                command: () => setSelectedApproveJob(job)
            });
        }

        if (canRequestAiJobCorrection(job)) {
            items.push({
                label: t('actions.request_change'),
                icon: "pi pi-comment",
                className: "p-button-help",
                disabled: correctionLoading,
                command: () => openAiJobCorrectionDialog(job)
            });
        }

        items.push({
            label: t('actions.delete'),
            icon: deletingAiJobId === job.id ? "pi pi-spin pi-spinner" : "pi pi-trash",
            className: "p-button-danger",
            disabled: !!deletingAiJobId,
            command: () => setSelectedDeleteJob(job)
        });
        items.push({separator: true});
        items.push({
            label: t('actions.refresh'),
            icon: "pi pi-refresh",
            command: () => refreshAiJob(job)
        });

        return items;
    };

    const runAiJobAction = (command) => {
        if (aiJobActionOverlayRef.current)
            aiJobActionOverlayRef.current.hide();
        command();
    };

    const openAiJobActionMenu = (event, job) => {
        setSelectedActionJob(job);
        if (aiJobActionOverlayRef.current)
            aiJobActionOverlayRef.current.toggle(event);
    };

    const aiJobActionOverlayTemplate = () => {
        const items = selectedActionJob ? getAiJobActionMenuItems(selectedActionJob) : [];

        return <OverlayPanel ref={aiJobActionOverlayRef} dismissable={true}>
            <div style={{minWidth: "14em"}}>
                {items.map((item, index) => item.separator
                    ? <hr key={index} style={{border: 0, borderTop: "1px solid #e5e7eb", margin: ".35em 0"}}/>
                    : <Button key={index}
                              label={item.label}
                              icon={item.icon}
                              className={"p-button-text " + (item.className || "")}
                              disabled={item.disabled}
                              onClick={() => runAiJobAction(item.command)}
                              style={{width: "100%", justifyContent: "flex-start", marginBottom: ".15em"}}/>
                )}
            </div>
        </OverlayPanel>;
    };

    const actionTemplate = (job) => {
        const isFixSpider = job.operation === "fix-spider";
        const isAddWebsiteSpider = job.operation === "add-website-spider";
        const isLoadingExceptionDetail = exceptionDetailLoadingJobId === job.id;
        const isLoadingWebsiteDetail = websiteDetailLoadingJobId === job.id;
        const exceptionHash = getNestedValue(job, ["input.exception_hash"]);
        const hasExceptionDetailAction = (isFixSpider && exceptionHash) || (!isFixSpider && job.exception_info);

        return <div style={{display: "flex", alignItems: "center", gap: ".5em", whiteSpace: "nowrap"}}>
            <Button icon="pi pi-info"
                    className="p-button-outlined p-button-info"
                    tooltip={t('actions.detail')}
                    tooltipOptions={{position: 'bottom'}}
                    onClick={() => openAiJobDetails(job)}/>
            {job.pull_request_url &&
                <Button icon="pi pi-external-link"
                        className="p-button-outlined p-button-success"
                        tooltip={t('actions.open_pr')}
                        tooltipOptions={{position: 'bottom'}}
                        onClick={() => window.open(job.pull_request_url, "_blank", "noopener,noreferrer")}/>}
            {hasExceptionDetailAction &&
                <Button icon={isLoadingExceptionDetail ? "pi pi-spin pi-spinner" : "pi pi-exclamation-circle"}
                        className="p-button-outlined p-button-warning"
                        tooltip={isFixSpider ? t('ai_job.exception_detail') : t('ai_job.exception_info')}
                        tooltipOptions={{position: 'bottom'}}
                        disabled={isLoadingExceptionDetail}
                        onClick={() => isFixSpider ? openFixSpiderExceptionDetail(job) : setSelectedExceptionInfo(job.exception_info)}/>}
            {isAddWebsiteSpider &&
                <Button icon={isLoadingWebsiteDetail ? "pi pi-spin pi-spinner" : "pi pi-globe"}
                        className="p-button-outlined p-button-warning"
                        tooltip={t('ai_job.website_detail')}
                        tooltipOptions={{position: 'bottom'}}
                        disabled={isLoadingWebsiteDetail}
                        onClick={() => openWebsiteDetail(job)}/>}
            <Button icon={isLoadingExceptionDetail ? "pi pi-spin pi-spinner" : "pi pi-ellipsis-v"}
                    className="p-button-outlined"
                    tooltip={t('actions.more_actions')}
                    tooltipOptions={{position: 'bottom'}}
                    onClick={(event) => openAiJobActionMenu(event, job)}/>
        </div>;
    };

    const getOptionsFromJobs = (field, fallbackValues, labelFormatter) => {
        const values = new Set(fallbackValues || []);
        aiJobs.forEach(job => {
            if (job[field])
                values.add(job[field]);
        });
        return [{label: t('actions.all'), value: null}, ...Array.from(values).sort().map(value => ({label: labelFormatter ? labelFormatter(value) : value, value: value}))];
    };

    const filteredAiJobs = sortAiJobs(aiJobs).filter(job => {
        return (!filters.status || job.status === filters.status) &&
            (!filters.provider || job.provider === filters.provider) &&
            (!filters.model_name || job.model_name === filters.model_name) &&
            (!filters.operation || job.operation === filters.operation);
    });

    const getAiJobLogActionLabel = (log) => {
        if (log.action === "job_queued")
            return t('ai_job.log_job_queued');
        if (log.action === "job_failed")
            return t('ai_job.log_job_failed');
        if (log.action === "review_correction_requested")
            return t('ai_job.log_review_correction_requested');
        if (log.action === "ai_provider_response" && log.payload && log.payload.request && log.payload.request.type === "initial_fix")
            return t('ai_job.log_initial_ai_response');
        if (log.action === "ai_provider_response" && log.payload && log.payload.request && log.payload.request.type === "review_correction")
            return t('ai_job.log_review_correction_ai_response');
        return log.action || "-";
    };

    const logPayloadTemplate = (log) => {
        const payload = log.payload || {};
        const response = payload.response || {};
        const diffSummary = response.diff_summary || {};
        const testsToRun = response.tests_to_run || [];

        return <div className="p-grid" style={{padding: ".5em 1em"}}>
            {payload.review_note &&
                <div className="p-col-12">
                    <b>{t('ai_job.review_note')}</b>
                    <div style={{whiteSpace: "pre-wrap", wordBreak: "break-word"}}>{payload.review_note}</div>
                </div>
            }
            {payload.request &&
                <div className="p-col-12">
                    <b>{t('ai_job.log_request')}</b>
                    <pre style={{whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: "12em", overflow: "auto"}}>{JSON.stringify(payload.request, null, 2)}</pre>
                </div>
            }
            {response.diagnosis &&
                <div className="p-col-12">
                    <b>{t('ai_job.diagnosis')}</b>
                    <div style={{whiteSpace: "pre-wrap", wordBreak: "break-word"}}>{response.diagnosis}</div>
                </div>
            }
            {(diffSummary.changed_files !== undefined || diffSummary.added_lines !== undefined || diffSummary.removed_lines !== undefined) &&
                <div className="p-col-12">
                    <b>{t('ai_job.log_diff_summary')}</b>
                    <div>
                        <span className="p-tag p-tag-info" style={{marginRight: ".5em"}}>{t('ai_job.changed_files')}: {diffSummary.changed_files !== undefined ? diffSummary.changed_files : "-"}</span>
                        <span className="p-tag p-tag-success" style={{marginRight: ".5em"}}>{t('ai_job.added_lines')}: {diffSummary.added_lines !== undefined ? diffSummary.added_lines : "-"}</span>
                        <span className="p-tag p-tag-warning">{t('ai_job.removed_lines')}: {diffSummary.removed_lines !== undefined ? diffSummary.removed_lines : "-"}</span>
                    </div>
                </div>
            }
            {response.tests_to_run &&
                <div className="p-col-12">
                    <b>{t('ai_job.tests_to_run')}</b>
                    {testsToRun.length > 0
                        ? <ul>{testsToRun.map((test, index) => <li key={index}>{test}</li>)}</ul>
                        : <div>-</div>}
                </div>
            }
            {response.diff &&
                <div className="p-col-12">
                    <details>
                        <summary>{t('ai_job.diff')}</summary>
                        <pre style={{whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: "18em", overflow: "auto", border: "1px solid #ddd", padding: "1em", marginTop: ".5em"}}>{response.diff}</pre>
                    </details>
                </div>
            }
            {Object.keys(payload).length === 0 &&
                <div className="p-col-12">-</div>
            }
        </div>;
    };

    const aiJobLogsTemplate = (job) => {
        const logs = sortAiJobLogs(job.logs);

        return <div className="p-col-12" style={{borderTop: "1px solid #ddd", marginTop: "1em", paddingTop: "1em"}}>
            <div className="p-grid">
                <div className="p-col-6">
                    <h5 style={{marginTop: 0, fontWeight: "bold"}}>{t('ai_job.operation_logs')}</h5>
                </div>
                <div className="p-col-6" style={{textAlign: "right"}}>
                    <Button label={t('actions.refresh_logs')}
                            icon={aiJobLogsLoading ? "pi pi-spin pi-spinner" : "pi pi-refresh"}
                            className="p-button-outlined"
                            disabled={aiJobLogsLoading}
                            onClick={() => refreshAiJobLogs(job.id)}/>
                </div>
                {logs.length === 0
                    ?
                    <div className="p-col-12">{t('ai_job.logs_empty')}</div>
                    :
                    <div className="p-col-12">
                        <DataTable value={logs} dataKey="id" responsive={true} expandedRows={expandedAiJobLogRows} onRowToggle={(e) => setExpandedAiJobLogRows(e.data)} rowExpansionTemplate={logPayloadTemplate}>
                            <Column expander={true} style={{width: "3em"}}/>
                            <Column field="created_at" header={t('ai_job.time')} body={(log) => dateTimeTemplate(log.created_at)} style={{width: "12em"}}/>
                            <Column field="action" header={t('ai_job.action')} body={(log) => getAiJobLogActionLabel(log)}/>
                            <Column field="provider" header={t('ai_job.provider')} style={{width: "8em"}}/>
                            <Column field="status" header={t('ai_job.status')} body={(log) => getStatusLabel(log.status)} style={{width: "12em"}}/>
                            <Column field="message" header={t('ai_job.message')} body={(log) => log.message || "-"}/>
                        </DataTable>
                    </div>
                }
            </div>
        </div>;
    };

    const aiJobDetailsDialogTemplate = () => {
        if (!selectedAiJobDetails)
            return null;

        return <Dialog header={t('ai_job.details')} visible={!!selectedAiJobDetails} style={{maxWidth: '70vw', minWidth: '45vw'}} modal={true}
                       onHide={() => setSelectedAiJobDetails(null)}>
            <div className="p-grid">
                <div className="p-col-12 p-md-6">
                    {statusTemplate(selectedAiJobDetails)}
                    <span className="p-tag p-tag-info" style={{marginLeft: ".5em"}}>{selectedAiJobDetails.provider}</span>
                    <span className="p-tag p-tag-info" style={{marginLeft: ".5em"}}>{selectedAiJobDetails.operation}</span>
                </div>
                {selectedAiJobDetails.pull_request_url &&
                    <div className="p-col-12 p-md-6" style={{textAlign: "right"}}>
                        <Button label={t('actions.open_pr')} icon="pi pi-external-link" className="p-button-success" onClick={() => window.open(selectedAiJobDetails.pull_request_url, "_blank", "noopener,noreferrer")}/>
                    </div>
                }
                <div className="p-col-12">
                    <b>{t('ai_job.summary')}</b>
                    <div>{summaryTemplate(selectedAiJobDetails)}</div>
                </div>
                <div className="p-col-12">
                    <b>{t('ai_job.exception_info')}</b>
                    <div>
                        <Button label={t('ai_job.exception_info')}
                                icon="pi pi-exclamation-circle"
                                className="p-button-outlined p-button-warning"
                                disabled={!selectedAiJobDetails.exception_info}
                                onClick={() => setSelectedExceptionInfo(selectedAiJobDetails.exception_info)}/>
                    </div>
                </div>
                <div className="p-col-12">
                    <b>{t('ai_job.branch_name')}</b>
                    <div style={{wordBreak: "break-word"}}>{selectedAiJobDetails.branch_name || "-"}</div>
                </div>
                <div className="p-col-12 p-md-4">
                    <b>{t('ai_job.created_at')}</b>
                    <div>{dateTimeTemplate(selectedAiJobDetails.created_at)}</div>
                </div>
                <div className="p-col-12 p-md-4">
                    <b>{t('ai_job.updated_at')}</b>
                    <div>{dateTimeTemplate(selectedAiJobDetails.updated_at)}</div>
                </div>
                <div className="p-col-12 p-md-4">
                    <b>{t('ai_job.verification_status')}</b>
                    <div>{selectedAiJobDetails.verification_status || "-"}</div>
                </div>
                <div className="p-col-12">
                    <b>{t('ai_job.change_summary')}</b>
                    <pre style={{whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: "18em", overflow: "auto"}}>{selectedAiJobDetails.diff || "-"}</pre>
                </div>
                <div className="p-col-12">
                    <b>{t('ai_job.diagnosis')}</b>
                    <pre style={{whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: "18em", overflow: "auto"}}>{selectedAiJobDetails.diagnosis || "-"}</pre>
                </div>
                {selectedAiJobDetails.error_message &&
                    <div className="p-col-12">
                        <b>{t('ai_job.error_message')}</b>
                        <div>
                            <span className="p-tag p-tag-danger" style={{whiteSpace: "normal"}}>{selectedAiJobDetails.error_message}</span>
                        </div>
                    </div>
                }
                {aiJobLogsTemplate(selectedAiJobDetails)}
            </div>
        </Dialog>;
    };

    return <React.Fragment>
        {aiJobActionOverlayTemplate()}
        {aiJobDetailsDialogTemplate()}
        {exceptionInfoDialogTemplate()}
        {websiteDetailDialogTemplate()}
        {exceptionDetailDialogTemplate()}
        {manualPromptDialogTemplate()}
        {aiJobCorrectionDialogTemplate()}
        <Dialog header={t('ai_job.approve_confirm_title')} visible={!!selectedApproveJob} style={{maxWidth: '35vw', minWidth: '28vw'}} modal={true}
                footer={approveAiJobConfirmationFooter()}
                onHide={() => !approvingAiJobId && setSelectedApproveJob(null)}>
            <div>{t('ai_job.approve_confirm_body')}</div>
        </Dialog>
        <Dialog header={t('ai_job.delete_confirm_title')} visible={!!selectedDeleteJob} style={{maxWidth: '35vw', minWidth: '28vw'}} modal={true}
                footer={deleteAiJobConfirmationFooter()}
                onHide={() => !deletingAiJobId && setSelectedDeleteJob(null)}>
            <div>{t('ai_job.delete_confirm_body')}</div>
        </Dialog>
        <div className="p-grid">
            <div className="p-col-12 p-md-2">
                <Dropdown value={filters.status}
                          options={getOptionsFromJobs("status", knownStatuses, getStatusLabel)}
                          onChange={(e) => setFilters({...filters, status: e.value})}
                          placeholder={t('ai_job.status')}
                          style={{width: "100%"}}/>
            </div>
            <div className="p-col-12 p-md-2">
                <Dropdown value={filters.provider}
                          options={getOptionsFromJobs("provider", ["gemini", "openai", "claude"])}
                          onChange={(e) => setFilters({...filters, provider: e.value})}
                          placeholder={t('ai_job.provider')}
                          style={{width: "100%"}}/>
            </div>
            <div className="p-col-12 p-md-2">
                <Dropdown value={filters.operation}
                          options={getOptionsFromJobs("operation", ["fix-spider"])}
                          onChange={(e) => setFilters({...filters, operation: e.value})}
                          placeholder={t('ai_job.operation')}
                          style={{width: "100%"}}/>
            </div>
            <div className="p-col-12 p-md-3">
                <Dropdown value={filters.model_name}
                          options={getOptionsFromJobs("model_name", [])}
                          onChange={(e) => setFilters({...filters, model_name: e.value})}
                          placeholder={t('ai_job.model')}
                          style={{width: "100%"}}/>
            </div>
            <div className="p-col-12 p-md-3" style={{textAlign: "right"}}>
                <Button label={t('actions.refresh')}
                        icon={loading ? "pi pi-spin pi-spinner" : "pi pi-refresh"}
                        className="p-button-outlined"
                        disabled={loading}
                        onClick={listAiJobs}
                        style={{marginRight: ".5em"}}/>
                <Button label={t('actions.clear_filters')}
                        icon="pi pi-filter-slash"
                        className="p-button-outlined"
                        disabled={loading}
                        onClick={() => setFilters(emptyFilters)}/>
            </div>
        </div>
        {loading
            ?
            <div className="p-grid p-col-12" style={{textAlign:'center'}}>
                <Loading/>
            </div>
            :
            <DataTable value={filteredAiJobs} dataKey="id" responsive={true} paginator={true} rows={20}
                       sortField="updated_at" sortOrder={-1} emptyMessage={t('ai_job.empty_all')}>
                <Column field="status" header={t('ai_job.status')} body={statusTemplate} style={{width: "12em"}}/>
                <Column field="provider" header={t('ai_job.provider')} style={{width: "8em"}}/>
                <Column field="model_name" header={t('ai_job.model')} body={(job) => job.model_name || "-"} style={{width: "12em"}}/>
                <Column field="operation" header={t('ai_job.operation')} style={{width: "9em"}}/>
                <Column header={t('ai_job.summary')} body={summaryTemplate} style={{width: "20em", maxWidth: "20em"}}/>
                <Column field="updated_at" header={t('ai_job.updated_at')} body={(job) => dateTimeTemplate(job.updated_at)} sortable={true} style={{width: "11em"}}/>
                <Column header={t('table_headers.action')} body={actionTemplate} style={{width: "11em"}}/>
            </DataTable>
        }
    </React.Fragment>;
};

export default AiJobsMonitor;
